import { useClientPortalAPI } from "@certn/api";
import {
  ActionListItem,
  Divider,
  FormLabelRAC,
  Grid,
  InputButton,
  LinkButton,
  Picker,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";

type User = {
  id: string;
  name: string;
};

type UserPickerProps = {
  label: string;
  selectedKeys: string[];
  isLoading?: boolean;
  isDisabled?: boolean;
  onChange: (userKeys: string[]) => void | Promise<void>;
  onRemove: (userId: string) => void | Promise<void>;
};

export function UserPicker({
  label,
  selectedKeys,
  onChange,
  onRemove,
  isLoading,
  isDisabled,
}: UserPickerProps) {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const { data: users, isLoading: isUsersLoading } = useClientPortalAPI(
    "/api/internal/client-portal/customer-accounts/users/compact/"
  );

  const filteredUsers = users?.reduce(
    (i: User[], user: Record<string, any>) => {
      // hide users from drop down once selected
      if (selectedKeys?.includes(user?.id)) return i;
      return [
        ...(i as User[]),
        {
          id: user?.id,
          name: user?.auth0_full_name,
          subtitle: user?.auth0_email,
        },
      ];
    },
    []
  );

  return (
    <Grid form>
      <Picker
        showActionBar
        hideClear
        triggerRef={triggerRef as React.RefObject<HTMLDivElement>}
        maxWidth="560px"
        items={filteredUsers}
        selectedKeys={selectedKeys}
        actionText={t("Add")}
        label={t("Add members")}
        selectionMode="multiple"
        isLoading={isLoading || isUsersLoading}
        testID="groups-users-picker"
        onSelectionChange={(values) => onChange(Array.from(values) as string[])}
        trigger={(props) => {
          return isDisabled ? null : (
            <div ref={triggerRef} className={styles.triggerWrapper}>
              <FormLabelRAC isRequired>{label}</FormLabelRAC>
              <InputButton
                onToggle={props?.onToggle}
                rightIcon="expand_more"
                isDisabled={isDisabled || !filteredUsers?.length}
              >
                <Text
                  style="input/text-field"
                  color={
                    props?.selectedItems?.length < 1
                      ? "text-placeholder"
                      : "text-base"
                  }
                >
                  {t("Select members")}
                </Text>
              </InputButton>
            </div>
          );
        }}
      />
      <Text style="input/label">
        {`${t("Members with access")} (${selectedKeys?.length})`}
      </Text>

      {users
        ?.filter(({ id }) => selectedKeys.includes(id))
        .map((user, index) => {
          const isLastUser = index === selectedKeys.length - 1;
          return (
            <React.Fragment key={index}>
              <ActionListItem
                padding="0px"
                title={user.auth0_full_name}
                trailing={
                  !isDisabled && (
                    <LinkButton
                      testID="remove-member-button"
                      onPress={() => onRemove(user.id)}
                      isLoading={isLoading}
                    >
                      <Text style="global/body-primary-link" color="text-weak">
                        {t("Remove")}
                      </Text>
                    </LinkButton>
                  )
                }
              >
                <Text style="global/body-secondary" color="text-weak">
                  {user.auth0_email}
                </Text>
              </ActionListItem>
              {!isLastUser && <Divider />}
            </React.Fragment>
          );
        })}
    </Grid>
  );
}

const styles = {
  triggerWrapper: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
};
